import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  FunnelIcon, 
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiHelper';
import { toast } from '../../utils/notification';
import { formatCurrency, formatHours, formatDate } from '../../utils';
import Tooltip from '../../components/ui/Tooltip';
import DataTable from '../../components/common/DataTable';

const TimeEntries = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    case: '',
    user: '',
    startDate: '',
    endDate: '',
    billable: '',
    invoiced: ''
  });
  const [cases] = useState([]);
  const [users] = useState([]);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  const [formData, setFormData] = useState({
    case: '',
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: '',
    is_billable: true,
    rate: ''
  });
  const [editingEntry, setEditingEntry] = useState(null);

  const fetchTimeEntries = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/billing/time-entries/';
      
      // Add any active filters to the request
      const queryParams = [];
      if (filters.case) queryParams.push(`case_id=${filters.case}`);
      if (filters.user) queryParams.push(`user_id=${filters.user}`);
      if (filters.startDate) queryParams.push(`start_date=${filters.startDate}`);
      if (filters.endDate) queryParams.push(`end_date=${filters.endDate}`);
      if (filters.billable) queryParams.push(`billable=${filters.billable}`);
      if (filters.invoiced) queryParams.push(`invoiced=${filters.invoiced}`);
      
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
      
      const data = await apiRequest(url);
      setTimeEntries(data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch time entries:', err);
      setError('Failed to load time entries. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTimeEntries();
  }, [filters, fetchTimeEntries]); // Add dependencies

  // Placeholder for future cases and users fetching
  // const fetchCasesAndUsers = async () => {
  //   try {
  //     const casesData = await apiRequest('/api/cases/');
  //     setCases(casesData || []);
  //     
  //     const usersData = await apiRequest('/api/users/');
  //     setUsers(usersData || []);
  //   } catch (err) {
  //     console.error('Failed to fetch cases or users:', err);
  //   }
  // };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const applyFilters = () => {
    fetchTimeEntries();
    setShowFilters(false);
  };

  const resetFilters = () => {
    setFilters({
      case: '',
      user: '',
      startDate: '',
      endDate: '',
      billable: '',
      invoiced: ''
    });
    // We don't call fetchTimeEntries here because we want to wait for the user to click "Apply Filters"
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      
      // If editing an existing entry
      if (editingEntry) {
        await apiRequest(`/api/billing/time-entries/${editingEntry.id}/`, 'PUT', payload);
        toast.success('Time entry updated successfully');
      } else {
        // Creating a new entry
        await apiRequest('/api/billing/time-entries/', 'POST', payload);
        toast.success('Time entry created successfully');
      }
      
      // Reset form and refresh data
      setFormData({
        case: '',
        date: new Date().toISOString().split('T')[0],
        hours: '',
        description: '',
        is_billable: true,
        rate: ''
      });
      setShowNewEntryForm(false);
      setEditingEntry(null);
      fetchTimeEntries();
    } catch (err) {
      console.error('Error saving time entry:', err);
      toast.error('Failed to save time entry. Please try again.');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      case: entry.case,
      date: entry.date,
      hours: entry.hours,
      description: entry.description,
      is_billable: entry.is_billable,
      rate: entry.rate
    });
    setShowNewEntryForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      await apiRequest(`/api/billing/time-entries/${id}/`, 'DELETE');
      setTimeEntries(timeEntries.filter(entry => entry.id !== id));
      toast.success('Time entry deleted successfully');
    } catch (err) {
      console.error('Error deleting time entry:', err);
      toast.error('Failed to delete time entry');
    }
  };

  const timeEntryColumns = [
    {
      key: 'date',
      label: 'Date',
      render: (row) => formatDate(row.date),
      sortValue: (row) => (row.date ? new Date(row.date).getTime() : 0),
    },
    {
      key: 'case',
      label: 'Case',
      className: 'text-gray-900 dark:text-white',
      render: (row) => row.case_detail?.title || 'Unknown Case',
      sortValue: (row) => row.case_detail?.title || '',
    },
    {
      key: 'description',
      label: 'Description',
      render: (row) => <div className="max-w-xs truncate">{row.description}</div>,
    },
    {
      key: 'user',
      label: 'User',
      render: (row) =>
        row.user_detail ? `${row.user_detail.first_name} ${row.user_detail.last_name}` : 'Unknown User',
      sortValue: (row) =>
        row.user_detail ? `${row.user_detail.first_name} ${row.user_detail.last_name}` : '',
    },
    {
      key: 'hours',
      label: 'Hours',
      headerClassName: 'text-right',
      className: 'text-right text-gray-500 dark:text-gray-300',
      render: (row) => formatHours(row.hours),
      sortValue: (row) => parseFloat(row.hours) || 0,
    },
    {
      key: 'amount',
      label: 'Amount',
      headerClassName: 'text-right',
      className: 'text-right text-gray-500 dark:text-gray-300',
      render: (row) => formatCurrency(row.amount),
      sortValue: (row) => parseFloat(row.amount) || 0,
    },
    {
      key: 'status',
      label: 'Status',
      headerClassName: 'text-center',
      className: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center space-x-2">
          {row.is_billable ? (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
              Billable
            </span>
          ) : (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
              Non-billable
            </span>
          )}
          {row.invoice && (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              Invoiced
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      headerClassName: 'text-right',
      className: 'text-right',
      render: (row) => (
        <>
          {!row.invoice && (
            <div className="flex justify-end space-x-2">
              <Tooltip content="Edit time entry" position="top">
                <button
                  onClick={() => handleEdit(row)}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                  aria-label={`Edit time entry: ${row.description}`}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              </Tooltip>
              <Tooltip content="Delete time entry" position="top">
                <button
                  onClick={() => handleDelete(row.id)}
                  className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                  aria-label={`Delete time entry: ${row.description}`}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </Tooltip>
            </div>
          )}
          {row.invoice && (
            <Tooltip content={`View invoice #${row.invoice}`} position="top">
              <Link
                to={`/billing/invoices/${row.invoice}`}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                aria-label={`View invoice #${row.invoice}`}
              >
                View Invoice
              </Link>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  if (loading && timeEntries.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Time Entries</h2>
        <div className="flex space-x-3">
          <Tooltip content="Filter time entries" position="bottom">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              aria-expanded={showFilters}
              aria-controls="filter-panel"
            >
              <FunnelIcon className="h-4 w-4 mr-1" />
              Filter
            </button>
          </Tooltip>
          
          <Tooltip content="Create new time entry (n t)" position="bottom">
            <button
              onClick={() => {
                setEditingEntry(null);
                setFormData({
                  case: '',
                  date: new Date().toISOString().split('T')[0],
                  hours: '',
                  description: '',
                  is_billable: true,
                  rate: ''
                });
                setShowNewEntryForm(!showNewEntryForm);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              aria-expanded={showNewEntryForm}
              aria-controls="new-time-entry-form"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              New Time Entry
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div id="filter-panel" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Filter Time Entries</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="case" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Case</label>
              <select
                id="case"
                name="case"
                value={filters.case}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Cases</option>
                {Array.isArray(cases) && cases.map(c => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="user" className="block text-sm font-medium text-gray-700 dark:text-gray-300">User</label>
              <select
                id="user"
                name="user"
                value={filters.user}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="billable" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Billable Status</label>
              <select
                id="billable"
                name="billable"
                value={filters.billable}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
                <option value="">All</option>
                <option value="true">Billable</option>
                <option value="false">Non-Billable</option>
              </select>
            </div>
            <div>
              <label htmlFor="invoiced" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Invoice Status</label>
              <select
                id="invoiced"
                name="invoiced"
                value={filters.invoiced}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
                <option value="">All</option>
                <option value="true">Invoiced</option>
                <option value="false">Not Invoiced</option>
              </select>
            </div>
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">End Date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Clear Filters
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* New/Edit Time Entry Form */}
      {showNewEntryForm && (
        <div id="new-time-entry-form" className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6 animate-fadeIn">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            {editingEntry ? 'Edit Time Entry' : 'New Time Entry'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="case" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Case <span className="text-red-500">*</span>
                </label>
                <select
                  id="case"
                  name="case"
                  value={formData.case}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a case</option>
                  {Array.isArray(cases) && cases.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="hours" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hours <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  id="hours"
                  name="hours"
                  value={formData.hours}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label htmlFor="rate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hourly Rate ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  id="rate"
                  name="rate"
                  value={formData.rate}
                  onChange={handleFormChange}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                  placeholder="If left blank, user's default rate will be used"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleFormChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                  placeholder="Describe the work performed..."
                ></textarea>
              </div>
              <div>
                <div className="flex items-center">
                  <input
                    id="is_billable"
                    name="is_billable"
                    type="checkbox"
                    checked={formData.is_billable}
                    onChange={handleFormChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_billable" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                    This time is billable
                  </label>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowNewEntryForm(false)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {editingEntry ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time Entries Table */}
      <DataTable
        columns={timeEntryColumns}
        data={timeEntries}
        searchable={false}
        defaultSort={{ key: 'date', direction: 'desc' }}
        emptyMessage="No time entries found. Get started by creating a new time entry."
        emptyIcon={ClockIcon}
        headerRight={
          <button
            className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center"
            onClick={fetchTimeEntries}
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" /> Refresh
          </button>
        }
      />
    </div>
  );
};

export default TimeEntries;
